'use client';

import { Table as HeroTable, Selection } from '@heroui/react';
import { ComponentType, ReactNode } from 'react';

import { Container } from './Container';
import { Text } from './Text';

const getAlignClass = (align?: 'start' | 'center' | 'end') => {
  if (align === 'center') return 'text-center';
  if (align === 'end') return 'text-right';

  return 'text-left';
};

export interface TableColumnConfig<T> {
  key: keyof T;
  heading: string;
  render?: ComponentType<{ value: T[keyof T]; row: T }>;
  allowsSorting?: boolean;
  align?: 'start' | 'center' | 'end';
}

export interface TableProps<T extends object> {
  ariaLabel: string;
  dataSet: T[];
  columns: TableColumnConfig<T>[];
  variant?: 'primary' | 'secondary';
  selectionMode?: 'none' | 'single' | 'multiple';
  onSelectionChange?: (keys: Selection) => void;
  selectedKeys?: Selection;
  renderEmptyState?: () => ReactNode;
}

export const Table = <T extends object>({
  ariaLabel,
  dataSet,
  columns,
  variant = 'primary',
  selectionMode = 'none',
  onSelectionChange,
  selectedKeys,
  renderEmptyState,
}: TableProps<T>) => (
  <Container>
    <HeroTable variant={variant}>
      <HeroTable.ScrollContainer className="bg-content1/40 backdrop-blur-md border border-divider p-4 shadow-none rounded-2xl overflow-auto">
        <HeroTable.Content
          aria-label={ariaLabel}
          selectionMode={selectionMode}
          onSelectionChange={onSelectionChange}
          selectedKeys={selectedKeys}
        >
          <HeroTable.Header columns={columns}>
            {(col) => (
              <HeroTable.Column
                key={col.key as string}
                isRowHeader={col.key === 'name' || col.key === 'id'}
                allowsSorting={col.allowsSorting}
                className={getAlignClass(col.align)}
              >
                {col.heading}
              </HeroTable.Column>
            )}
          </HeroTable.Header>
          <HeroTable.Body items={dataSet} renderEmptyState={renderEmptyState}>
            {(row) => (
              <HeroTable.Row key={(row as any).id ?? JSON.stringify(row)}>
                {columns.map((col, index) => {
                  const value = row[col.key];
                  const CellRenderer = col.render;

                  return (
                    <HeroTable.Cell
                      key={col.key as string}
                      className={`${getAlignClass(col.align)} ${index === 0 ? 'rounded-l-2xl' : ''} ${index === columns.length - 1 ? 'rounded-r-2xl' : ''}`}
                    >
                      {CellRenderer ? (
                        <CellRenderer value={value} row={row} />
                      ) : (
                        <Text variant="body">
                          {value !== null && value !== undefined
                            ? String(value)
                            : '—'}
                        </Text>
                      )}
                    </HeroTable.Cell>
                  );
                })}
              </HeroTable.Row>
            )}
          </HeroTable.Body>
        </HeroTable.Content>
      </HeroTable.ScrollContainer>
    </HeroTable>
  </Container>
);
